import { FileText, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OrchestrationHierarchyTree } from '@/components/learningTutorial/OrchestrationHierarchyTree';

/**
 * Shows how timeline JSON links back to the Pass 3 trainer script (project.script).
 */
export function TimelineScriptSourcePanel({
  timeline,
  script,
  orchestration,
  viewMode = 'outline',
  onOpenScriptStep,
}) {
  const scriptRef = timeline?.script_ref;
  const scriptPayload = script || orchestration?.pass_history?.pass3?.script || orchestration?.script;
  const topicTree = scriptPayload?.scenes?.[0]?.topic_tree || [];

  if (!scriptRef && !scriptPayload) {
    return (
      <p className="text-xs text-slate-500 rounded-sm border border-slate-700/60 bg-slate-900/50 px-3 py-2">
        Trainer script not loaded — timeline blocks were generated from Pass 3 narration text.
      </p>
    );
  }

  const digest = scriptRef?.source_script_digest;
  const narrCount = scriptRef?.trainer_narration_count;

  return (
    <div className="space-y-3 rounded-sm border border-sky-500/25 bg-sky-950/20 px-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-sky-200 flex items-center gap-1.5">
            <Link2 className="w-3.5 h-3.5 shrink-0" />
            Source: Pass 3 trainer script
          </p>
          <p className="text-sm text-slate-200 mt-1 truncate">
            {scriptRef?.title || scriptPayload?.title || 'Trainer script'}
          </p>
          <p className="text-[11px] text-slate-500 mt-1">
            Stored on <span className="font-mono text-slate-400">project.script</span>
            {digest ? (
              <span> · digest <span className="font-mono text-slate-400">{digest}</span></span>
            ) : null}
            {narrCount != null ? (
              <span> · {narrCount} trainer narration(s)</span>
            ) : null}
            {scriptRef?.topic_tree_roots != null ? (
              <span> · {scriptRef.topic_tree_roots} topic root(s)</span>
            ) : null}
          </p>
          <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
            Timeline JSON is the presentation layer (blocks, scenes, sync timings).
            Narration text is copied verbatim from <span className="font-mono text-slate-400">trainer_narration</span>
            {' '}on each DFS node — the full script tree is not embedded in timeline.
          </p>
        </div>
        {onOpenScriptStep ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 rounded-sm border-sky-500/40 text-sky-200 hover:bg-sky-500/10"
            onClick={onOpenScriptStep}
            data-testid="timeline-open-script-step"
          >
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            View script step
          </Button>
        ) : null}
      </div>

      {viewMode === 'outline' && topicTree.length > 0 ? (
        <div className="border-t border-sky-500/15 pt-3">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Trainer topic tree (script)</p>
          <OrchestrationHierarchyTree
            tabId="script"
            data={{ script: scriptPayload, scenes: scriptPayload?.scenes }}
          />
        </div>
      ) : null}
    </div>
  );
}
